import { useState } from 'react'
import { Link } from 'react-router-dom'
import { exportsApi, type ExportRequest } from '../api/exports'
import { Button, Callout, Dialog, FormGrid, TextField } from './ui'

const REASON_MAX_LENGTH = 500

/**
 * F-117 (REQ-266-001): finestra "Richiedi approvazione per l'export". Si apre quando il server risponde 428
 * `EXPORT_APPROVAL_REQUIRED`: chiede il motivo e crea la richiesta con i filtri correnti. Un amministratore
 * DIVERSO da chi chiede la approva in Approvazioni > Export; poi chi ha chiesto scarica da lì (uso singolo).
 */
export function ExportApprovalDialog({
  dataset,
  datasetLabel,
  filters,
  onClose,
}: {
  dataset: string
  datasetLabel: string
  filters: Record<string, string>
  onClose: () => void
}) {
  const [reason, setReason] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState('')
  const [created, setCreated] = useState<ExportRequest | null>(null)

  const trimmed = reason.trim()

  async function submit(e: React.FormEvent) {
    e.preventDefault()
    if (!trimmed) return
    setBusy(true)
    setError('')
    try {
      setCreated(await exportsApi.create({ dataset, filters, reason: trimmed }))
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Errore durante la richiesta')
    } finally {
      setBusy(false)
    }
  }

  return (
    <Dialog
      open
      onClose={onClose}
      title="Richiedi approvazione per l’export"
      supportingText={
        created ? undefined : (
          <>
            L’export di <strong>{datasetLabel}</strong> richiede l’approvazione di un amministratore. Indica perché ti serve:
            il motivo resta nel registro di audit.
          </>
        )
      }
      as="form"
      onSubmit={e => void submit(e)}
      actions={
        <>
          <Button variant="text" dismiss onClick={onClose}>
            {created ? 'Chiudi' : 'Annulla'}
          </Button>
          {!created && (
            <Button type="submit" variant="text" disabled={!trimmed || busy}>
              {busy ? 'Invio…' : 'Invia richiesta'}
            </Button>
          )}
        </>
      }
    >
      {created ? (
        <Callout tone="success">
          Richiesta inviata: <strong>{datasetLabel}</strong>. Un amministratore la deve approvare; poi la scarichi da{' '}
          <Link to="/approvals">Approvazioni → Export</Link> (vale per un solo download, con gli stessi filtri).
        </Callout>
      ) : (
        <>
          {error && <Callout tone="error">{error}</Callout>}
          <FormGrid columns={1}>
            <TextField
              multiline
              rows={3}
              label="Motivo della richiesta"
              maxLength={REASON_MAX_LENGTH}
              value={reason}
              onChange={e => setReason(e.target.value)}
              supportingText={`${reason.length}/${REASON_MAX_LENGTH}`}
              autoFocus
            />
          </FormGrid>
        </>
      )}
    </Dialog>
  )
}
